/**
 * Fuente ÚNICA del cálculo de "asistentes confirmados" (008 · US1 · FR-001 · R6).
 *
 * Fórmula canónica: por cada invitado CONFIRMADO, sumar `rsvp_guests_count` cuando es > 0;
 * si es 0/null (confirmó sin indicar cuántos), contar 1 (el propio invitado).
 *
 * No se usa `passes_count` como fallback (sobre-cuenta: asume que todos usan todos sus pases)
 * ni 0 (sub-cuenta: ignora confirmados sin conteo). Es el número que ve el catering, y DEBE
 * ser idéntico en todas las pantallas (dashboard central, vista por invitación, widget).
 */
export interface AttendeeCountable {
  rsvp_status: string;
  rsvp_guests_count?: number | null;
  passes_count?: number | null;
}

export function confirmedAttendees(
  guests: readonly AttendeeCountable[] | null | undefined,
): number {
  if (!guests) return 0;
  return guests.reduce((sum, g) => {
    if (g.rsvp_status !== 'confirmed') return sum;
    const count = g.rsvp_guests_count ?? 0;
    return sum + (count > 0 ? count : 1);
  }, 0);
}

/**
 * Asistentes PENDIENTES: por cada invitado en estado `pending`, sumar sus pases
 * reservados (`passes_count`); si es 0/null, contar 1 (una invitación es para al
 * menos una persona). Los pendientes aún no indicaron cuántos vendrán, así que sus
 * pases son la mejor estimación de asistentes por confirmar.
 *
 * Los `declined` (rechazados) NO se cuentan — misma regla que `confirmedAttendees`:
 * un rechazo no aporta asistentes a ningún cálculo.
 */
export function pendingAttendees(
  guests: readonly AttendeeCountable[] | null | undefined,
): number {
  if (!guests) return 0;
  return guests.reduce((sum, g) => {
    if (g.rsvp_status !== 'pending') return sum;
    const count = g.passes_count ?? 0;
    return sum + (count > 0 ? count : 1);
  }, 0);
}

/**
 * Asistentes esperados = confirmados + pendientes. Excluye rechazados (declined).
 * Es el número GLOBAL que la pareja compara contra la capacidad del salón: cuánta
 * gente vendría si todos los pendientes asistieran con sus pases reservados.
 */
export function expectedAttendees(
  guests: readonly AttendeeCountable[] | null | undefined,
): number {
  return confirmedAttendees(guests) + pendingAttendees(guests);
}
